import { type InfiniteData, type QueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { merge } from 'lodash';

import { type Asset, type ListResponse, type ShortUrlResponse, type WorkspaceInfo } from '@air/api/types';

import {
  type AssetScopedShareLinkListItem,
  getAssetShareLinksListKey,
} from '~/swr-hooks/shortUrl/useListAssetShareLinks';

type AssetShareLinksListCache = InfiniteData<ListResponse<AssetScopedShareLinkListItem>>;

export const findAssetShareLinkInListCache = (
  queryClient: QueryClient,
  params: { workspaceId: WorkspaceInfo['id']; assetId: Asset['id'] },
  shortId: string,
): AssetScopedShareLinkListItem | undefined => {
  const list = queryClient.getQueryData<AssetShareLinksListCache>(getAssetShareLinksListKey(params));

  if (!list) {
    return undefined;
  }

  for (const page of list.pages) {
    const item = page?.data?.find((row) => row.id === shortId);

    if (item) {
      return item;
    }
  }

  return undefined;
};

export const updateAssetShareLinkInListCache = (
  shortId: string,
  updates: Partial<ShortUrlResponse>,
  currentCache: AssetShareLinksListCache,
  options?: { newId?: string },
): AssetShareLinksListCache =>
  produce(currentCache, (draft) => {
    for (const page of draft.pages) {
      if (!page?.data) {
        continue;
      }

      const itemIndex = page.data.findIndex((item) => item.id === shortId);

      if (itemIndex !== -1) {
        page.data[itemIndex] = merge({}, page.data[itemIndex], updates, {
          ...(options?.newId && { id: options.newId }),
        });
        break;
      }
    }
  });

export const patchAssetShareLinksListQueryData = (
  queryClient: QueryClient,
  params: { workspaceId: WorkspaceInfo['id']; assetId: Asset['id'] },
  shortId: string,
  updates: Partial<ShortUrlResponse>,
  options?: { newId?: string },
) => {
  const listKey = getAssetShareLinksListKey(params);

  queryClient.setQueryData<AssetShareLinksListCache | undefined>(listKey, (old) =>
    old ? updateAssetShareLinkInListCache(shortId, updates, old, options) : old,
  );
};
